import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import ProviderBuilder from 'assets/providers/ProviderBuilder';
import { compact } from 'lodash';
import dealerListApi from 'models/dealerManagement/dealer/list/Api.Dealer.List';
import pricingDistributionTemplateListApi from 'models/productManagement/pricingDistributionTemplate/list/Api.PricingDistributionTemplate.List';
import distributorListApi from 'models/sales/distributor/list/Api.Distributor.List';
import { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

type PriceSelectionToolsProps = {
  id?: number;
  dataType?: Module.ProductManagement.PriceAdjustments.DataType;
  dealerId?: number;
  distributorId?: number;
  distributorTemplateId?: number;
  alreadyUsedTemplateId?: number;
  onSelect(props: { dealerId?: number; distributorId?: number; distributorTemplateId?: number }): void;
};
function usePriceSelectionToolsCore({ id, dataType, onSelect, ...restProps }: PriceSelectionToolsProps) {
  const { user, isDealer, isDistributor } = useAuthStore();

  const [dealers, setDealers] = useState<Model.IDealer[]>([]);
  const [distributors, setDistributors] = useState<Model.IDistributor[]>([]);
  const [templates, setTemplates] = useState<Model.IPricingDistributionTemplate[]>([]);

  useEffect(() => {
    (async () => {
      function isValidDistributor(distributor: Model.IDistributor) {
        return dataType === 'product'
          ? distributor.distributorProducts?.some((p) => p.productId === id)
          : distributor.distributorProductEnhancements?.some((p) => p.productEnhancementId === id);
      }
      if (isDealer) {
        const dealerIds = compact(user?.dealers?.map((it) => it.toJSON())).map((d) => d.id);
        if (user?.distributorId && dealerIds.length) {
          const distributors = (
            await distributorListApi({
              id: [user.distributorId],
              productId: dataType === 'product' ? [id] : undefined,
              productEnhancementId: dataType === 'productEnhancement' ? [id] : undefined,
            })
          ).payload?.data;
          const dealers = (
            await dealerListApi({
              id: dealerIds,
              productId: dataType === 'product' ? [id] : undefined,
              productEnhancementId: dataType === 'productEnhancement' ? [id] : undefined,
            })
          ).payload?.data;
          const templates = (
            await pricingDistributionTemplateListApi({
              distributorId: distributors.map((d) => d.id),
            })
          )?.payload?.data;

          unstable_batchedUpdates(() => {
            setDealers(compact(dealers));
            setDistributors(compact(distributors));
            setTemplates(compact(templates));
          });
        }
      } else if (isDistributor) {
        if (user?.distributorId) {
          const distributors = (
            await distributorListApi({
              id: [user.distributorId],
              productId: dataType === 'product' ? [id] : undefined,
              productEnhancementId: dataType === 'productEnhancement' ? [id] : undefined,
            })
          ).payload?.data;
          const dealers = (
            await dealerListApi({
              distributorId: [user.distributorId],
              productId: dataType === 'product' ? [id] : undefined,
              productEnhancementId: dataType === 'productEnhancement' ? [id] : undefined,
            })
          ).payload?.data;
          const templates = (
            await pricingDistributionTemplateListApi({
              distributorId: distributors.map((d) => d.id),
            })
          )?.payload?.data;

          unstable_batchedUpdates(() => {
            setDealers(compact(dealers));
            setDistributors(compact(distributors));
            setTemplates(compact(templates));
          });
        }
      } else {
        const dealers = (
          await dealerListApi({
            productId: dataType === 'product' ? [id] : undefined,
            productEnhancementId: dataType === 'productEnhancement' ? [id] : undefined,
          })
        ).payload?.data;
        const distributors = (
          await distributorListApi({
            productId: dataType === 'product' ? [id] : undefined,
            productEnhancementId: dataType === 'productEnhancement' ? [id] : undefined,
          })
        ).payload?.data;
        const templates = (
          await pricingDistributionTemplateListApi({
            distributorId: distributors.map((d) => d.id),
          })
        )?.payload?.data;

        unstable_batchedUpdates(() => {
          setDealers(compact(dealers));
          setDistributors(compact(distributors).filter(isValidDistributor));
          setTemplates(compact(templates));
        });
      }
    })();
  }, [id, dataType]);

  return {
    dataType,
    dataTypeId: id,
    onSelect,
    dealers,
    distributors,
    templates,
    dealer: dealers.find((d) => d.id === restProps.dealerId),
    distributor: distributors.find((d) => d.id === restProps.distributorId),
    distributorTemplate: templates.find((d) => d.id === restProps.distributorTemplateId),
    alreadyUsedTemplateId: restProps.alreadyUsedTemplateId,
  };
}

const { useProvider: usePriceSelectionTools, Provider: PriceSelectionToolsProvider } =
  ProviderBuilder(usePriceSelectionToolsCore);

export { usePriceSelectionTools, PriceSelectionToolsProvider };
